import * as yup from "yup";
import { LoadingSkeleton } from "@components/skeleton-loading";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FaKey, FaQuestionCircle } from "react-icons/fa";
import { FiKey, FiMail, FiSmartphone } from "react-icons/fi";
import { Button } from "@components/buttons/button";
import { InputGroup } from "@components/forms/input-group";
import {
  IFormPasswordLogin,
  useAuthContext,
} from "@hooks/contexts/auth-context";
import { formatName } from "@hooks/functions/formatName";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
}

type ILoginOptions = "password" | "email" | "phone";

export function ModalClientLogin({ isOpen, onClose }: IProps) {
  const { client, passwordSignIn } = useAuthContext();

  const [animate, setAnimate] = useState(false);
  const [activeTab, setActiveTab] = useState<ILoginOptions>("password");
  const [isLoading, setIsLoading] = useState(false);
  const [isLogging, setIsLogging] = useState(false);

  const scheme = yup.object().shape({
    password: yup.string().required("Senha é obrigatório"),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormPasswordLogin>({
    resolver: yupResolver(scheme) as any,
    mode: "onSubmit",
    reValidateMode: "onBlur",
  });

  async function handlePasswordLogin(data: IFormPasswordLogin) {
    setIsLogging(true);
    try {
      await passwordSignIn(data);

      setTimeout(() => {
        handleCancel();
      }, 1500);
    } catch (err: any) {
      setIsLogging(false);
    }
  }

  function handleEscape(evt: KeyboardEvent) {
    if (evt.key === "Escape") handleCancel();
  }

  function handleCancel() {
    setAnimate(false);
    setTimeout(() => onClose(), 300);
  }

  // Block the Scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Animation + ESC
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    } else {
      setAnimate(false);
    }
  }, [isOpen]);

  return (
    <>
      <div
        className={`modal fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        aria-hidden={!isOpen}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            {/* Header */}
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                <FiKey className="fs-5" />
                Autenticação
              </h5>
              <button className="btn-close" onClick={handleCancel} />
            </div>

            <div className="d-flex align-items-center px-3 pt-3">
              <span className="">
                <FaQuestionCircle /> Como Deseja se Autenticar?
              </span>
            </div>

            {/* Tabs */}
            <div className="px-3 pt-2">
              <ul className="nav nav-tabs">
                <li className="nav-item">
                  <button
                    className={`d-flex align-items-center nav-link ${activeTab === "password" ? "active" : ""}`}
                    onClick={() => setActiveTab("password")}
                  >
                    <FaKey className="me-2" />
                    Senha
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`d-flex align-items-center nav-link ${activeTab === "email" ? "active" : ""}`}
                    onClick={() => setActiveTab("email")}
                  >
                    <FiMail className="me-2" />
                    E-mail
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`d-flex align-items-center nav-link ${activeTab === "phone" ? "active" : ""}`}
                    onClick={() => setActiveTab("phone")}
                  >
                    <FiSmartphone className="me-2" />
                    Celular
                  </button>
                </li>
              </ul>
            </div>

            {/* Body */}
            <div className="modal-body">
              {activeTab === "password" && (
                <>
                  {isLoading ? (
                    <LoadingSkeleton />
                  ) : (
                    <>
                      <div className="row g-3">
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleSubmit(handlePasswordLogin)();
                          }}
                        >
                          <div className="row d-flex justify-content-center">
                            <div className="col-md-6 d-flex flex-column justify-content-center">
                              <div className="alert alert-info text-center mt-3 mb-3">
                                Autenticando como{" "}
                                <span className="fw-bold">
                                  {formatName(client?.client.name || "")}
                                </span>
                                :
                              </div>
                              <div className="text-muted mt-3 mb-3">
                                Insira a sua senha de autenticação.
                              </div>

                              <InputGroup
                                label="Senha"
                                animated
                                type="password"
                                placeholder="******"
                                register={register("password")}
                                errorMessage={errors?.password?.message}
                              />

                              <div className="d-flex justify-content-between gap-2 mb-1">
                                <div>
                                  <a href="#recovery" className="text-white">
                                    Esqueceu sua senha?
                                  </a>
                                </div>
                              </div>

                              <div className="d-flex justify-content-between gap-2">
                                <Button
                                  type="submit"
                                  className="btn btn-primary w-100"
                                  loading={isLogging}
                                >
                                  Entrar
                                </Button>
                              </div>
                            </div>
                          </div>
                        </form>
                      </div>
                    </>
                  )}
                </>
              )}

              {activeTab === "email" && (
                <div className="text-center py-5 text-muted">
                  <p>Em construção...</p>
                </div>
              )}

              {activeTab === "phone" && (
                <div className="text-center py-5 text-muted">
                  <p>Em construção...</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer border-0">
              <button
                className="btn btn-outline-secondary px-5"
                onClick={handleCancel}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Backdrop */}
      <div
        className={`modal-backdrop fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        onClick={handleCancel}
      />
    </>
  );
}
